import { useRef, useState } from 'react';
import { usePersonalizationOptional } from '../lib/use_personalization';
import { useUnsavedChanges } from '../lib/use_unsaved_changes';

export function useDirtyForm<T extends Record<string, unknown>>(initialValues: T) {
  const original = useRef<T>(initialValues);
  const [values, setValues] = useState<T>(initialValues);
  const isDirty = JSON.stringify(values) !== JSON.stringify(original.current);

  // Mark this form's workspace tab as having unsaved changes. The owning page
  // code comes from the personalization context (every page provides it), so a
  // form inside any page — including a portalled edit modal — marks the correct
  // tab regardless of which tab is currently focused. No-ops outside a page/shell.
  const personalization = usePersonalizationOptional();
  useUnsavedChanges(personalization?.pageCode ?? null, isDirty);

  const reset = (v?: T) => {
    const next = v ?? initialValues;
    original.current = next;
    setValues(next);
  };

  // Typed updater: accepts either a full new value or a function (T => T).
  // Using this instead of the raw setValues avoids implicit-any on the `f`
  // parameter when TypeScript cannot infer the updater function's argument type
  // from a constrained generic.
  const update = (patch: T | ((prev: T) => T)) => {
    if (typeof patch === 'function') {
      setValues(prev => (patch as (prev: T) => T)(prev));
    } else {
      setValues(patch);
    }
  };

  return { values, setValues: update, isDirty, reset };
}
